import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FormSectionElements, ProgramContainer } from '../../entities/formTemplateDataType';
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { ErrorMessage } from './AddressComponent';
import { useAddressSearch } from '../../services/AddressSearchHooks';
import AddressDetails from './AddressDetails';
import ErrorElement from '../../commonComponent/ErrorElement';
import { getAddress } from '../../utilities/addressUtils';
import { findMatchedAddressElement, getRootComponent } from '../../utilities/ElementUtils';
import FormValidationUtils from '../../utilities/FormValidationUtils';
import { add, get, set, isEqual } from 'lodash';
import colors from '../../../src/theme/variables/colors';
import { HintExpansionComponent } from '../odsCalloutBox/HintExpansionComponent';
import { translations } from "../../entities/Enums";
import validator from 'validator';
import { getEnvVar } from "../../config/RuntimeConfig";
import { isBxpForm } from "../../utilities/ElementUtils";
import {
    getMockAddressDetails,
    getMockAddressSearchResponse,
    getMockPostalLookupResponse,
    isE2EBypassEnabled,
} from '../../utilities/E2EBypassUtils';


export class Address {
    address1: string;
    address2?: string;
    streetName?: string;
    streetType?: string;
    streetNumber?: string;
    streetNumberSuffix?: string;
    streetDirection?: string;
    streetFromNumber?: string;
    streetToNumber?: string;
    streetFromSuffix?: string;
    streetToSuffix?: string;
    suiteNumber?: string;
    suiteFromNumber?: string;
    suiteToNumber?: string;
    municipality: string;
    directoryArea?: string;
    province: string;
    provinceOther?: string;
    postalCode: string;
    stationName?: string;
    stationType?: string;
    poBoxKeyword?: string;
    poBoxNumber?: string;
    ruralRoute?: string;
    manualAddressChecked?: boolean;
    country?: string;
    poBoxFromNumber?: string;
    poBoxToNumber?: string;
    routeType?: string;
    routeNumber?: string;
    deliveryInstallationArea?: string;
    deliveryInstallationName?: string;
    deliveryInstallationType?: string;
    typeOfAddress?: Number;
    mailAddressType?: number;
    unitType?: UNIT_TYPE;
    streetSequence?: string;
    type?: number;
    description?: string;
    lotType?: LOT_TYPE;
    lotNumber?: string;
    identifier?: PROPERTY_IDENTITFIER;
    identifierNumber?: string;
    township?: string;
    isNoStreetNoAddress?: boolean;
    commaAfterStreetNumber?: boolean;
    isDetailAddress = true;
  };

export interface AddressSearchProps {
    lang: 'en' | 'fr';
    setFieldValue: (val: string) => void;
    fieldName: string;
    searchUrl: string;
    field: FormSectionElements;
    setFieldValid: (valid: boolean) => void;
    setFieldAddress: (address: Address) => void;
    setSameAsResidential: (value: boolean) => void;
    setValidationErrorMessage: (errors: ErrorMessage[]) => void;
    callback?: (programContainer: ProgramContainer)=> void;
    accessToken?: string
}

export type UNIT_TYPE = 'APT' | 'SUITE' | 'UNIT' | '';
export type LOT_TYPE = '' | 'L' | 'P' | 'B' | 'K';
export type PROPERTY_IDENTITFIER = 'CN' | 'PN' | '';

const propts = {
    BXPstreetNo: {en: 'Start typing any address or postal code', fr: 'Commencez par taper une adresse ou un code postal'},
    streetNo: {en: 'Start typing any address or postal code', fr: 'Commencez à taper une adresse ou code postal'},
    noStreetNo: {en: 'Start typing any postal code', fr: 'Commencez à taper un code postal'},
    title: {en: 'My address does not have a street number or street name', fr: 'Mon adresse ne comporte ni numéro municipal ni nom de rue'},
    unknownAddress: {en: "I can't find the address or I don't know the full address", fr: "Je ne trouve pas l'adresse ou je ne connais pas l'adresse complete"},
    isBusiness: {en: 'Business address does not have a street number or street name', fr: 'L\'adresse de l\'entreprise ne comporte ni numéro municipal ni nom de rue'},
    sameMaling: {en: 'Mailing address is same as residential', fr: 'L\'adresse postale est la même que l\'adresse résidentielle'},
  };

const AddressSearchComponent = ({
    lang,
    fieldName,
    searchUrl,
    field,
    setFieldValue,
    setFieldValid,
    setFieldAddress,
    setSameAsResidential,
    setValidationErrorMessage,
    callback,
    accessToken,
}: AddressSearchProps) => {
    const profile = getRootComponent(field).Profile;
    const isPCSearch = (field.Address?.isDetailAddress || profile?.address?.isDetailAddress) ? (field.Address?.isNoStreetNoAddress || profile?.address?.isNoStreetNoAddress) : false
    let addressString = (field.addressType === 'Mailing' && field.mailingSameAsResidential) || field.addressType==='Residential'
        ? ((isPCSearch ? (field.Address?.postalCode || profile?.address?.postalCode) : field.Value) 
            || getAddress(field.Address || profile?.address, field.Address?.isNoStreetNoAddress ?? profile?.address?.isNoStreetNoAddress, lang))
        : (field.Value || getAddress(field.Address, field.Address?.isNoStreetNoAddress, lang));
    const [timeoutIds, setTimeoutIds] = useState([]);
    const [value, setValue] = useState(addressString);
    const typedStreetNumberRef = useRef<string | null>(null);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [noStreetNo, setNoStreeNo] = useState(isPCSearch);
    const [sameMailing, setSameMailing] = useState(field.mailingSameAsResidential);
    const [addressDetails, setAddressDetails] = useState<Address|null>(field?.Address?.isDetailAddress ? {...field.Address, commaAfterStreetNumber: field.commaAfterStreetNumber} : null);
    const [errors, setErrors] = useState<ErrorMessage[]>([]);
    const [manualEntry, setManualEntry] = useState(!!field?.manualAddressChecked || !!field?.unknownAddressChecked);
    const [manualAddressInitialized, setManualAddressInitialized] = useState(false);
    const [isPostalCode, setIsPostalCode] = useState(false);
    const [unknownAddressChecked, setUnknownAddressChecked] = useState(!!field?.unknownAddressChecked);
    const manualAddressEntryLabel = (
        (lang === 'en' ? field.manualAddressEntryLabel : field.manualAddressEntryLabelFRA) ||
        (lang === 'en' ? 'Enter address manually' : 'Entrer l’adresse manuellement')
    );
    const e2eBypass = isE2EBypassEnabled();
    const allowManualEntry = !!field.allowManualAddress || !!field.unknownAddressChecked;
    const { data, loading, error, setSearchingAddress } = useAddressSearch(
        addressString,
        getEnvVar('PC_LOOKUP_URL')|| searchUrl,
        addressDetails? addressDetails.isNoStreetNoAddress : profile?.address?.isDetailAddress ? profile.address.isNoStreetNoAddress : false,
        isPCSearch && profile?.address?.postalCode===addressString || field?.manualAddressChecked || field?.Address,
        accessToken,
        field as {CanadianAddress: boolean},
        field?.hideCountry
    );
    const searchDelay = 1000;

    const getHeader = () => {
        let header: any = {
            "Content-Type": "application/json",
        }
        if (accessToken) {
            header = {...header, "Authorization": `Bearer ${accessToken}`}
        }
        return header;
    }

    const search = (e: AutoCompleteCompleteEvent) => {
        if (e.originalEvent.nativeEvent["inputType"] == "deleteContentBackward" && field.isBXPaddress)
           return;
        const value = e.query;
        //skip if only number or spaces and less than 3 characters
        if (value?.length < 3 || /^[0-9 ]+$/.test(value)) {
            return;
        }
        if (noStreetNo) {
            searchWithPostalCode(value);
        } else {
            searchWithAddress({
                "addressString": value,
                "province": field?.CanadianAddress ? null : "ON",
                "displayAddressLines": true
            })
        }
    }

    const searchWithAddress = (payload: {
        "addressString": string;
        "province": string,
        "displayAddressLines": boolean,
    }) => {
        const searchId = setTimeout(() => {
            try {
                const request = isE2EBypassEnabled()
                    ? Promise.resolve({ data: getMockAddressSearchResponse(payload.addressString) })
                    : axios.post(searchUrl, payload, {headers: getHeader()});

                request.then(res=>{
                    const data = res.data; 
                    if (data.status === 'OK') {
                        setSuggestions(filterByProv(data.mailAddressChoice?.addressAlternatives, payload) as string[]);
                        const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
                        const isPostalCodeInput = postalCodeRegex.test(payload.addressString.trim());
                        setIsPostalCode(isPostalCodeInput);
                    }else {
                        setSuggestions([]);
                    }
                })
            } catch (error) {
                console.error("API call failed:", error);
            }
        }, searchDelay);

        setTimeoutIds(prev => [...prev, searchId]);
    };

    const filterByProv = (addresses: string[], payload: any): string[] => {
        if (!payload?.province) {
            return addresses;
        }
        // Assuming that the return addresses are in '222 Jarvis Street, ON' or '222 Jarvis Street, ON, M1M1M1' formats.
        const filterString = ', ' + payload.province;
        return addresses.filter((addr) => addr.includes(filterString));
    }

    const searchWithPostalCode = (postalCode: string) => {
        const id = 'searchInput';
        const normalizedPostalCode = postalCode?.trim();

        if (!normalizedPostalCode) {
            removeErrorMessage(id);
            return;
        }

        // Only search when a full postal code is entered (e.g. A1A 1A1)
        if (!/^[a-z][0-9][a-z]\s*[0-9][a-z][0-9]$/i.test(normalizedPostalCode)) {
            addErrorMessage(id, lang==='en'? 'Invalid postal code': 'Code postal invalide');
            return;
        }

        if (normalizedPostalCode === addressDetails?.postalCode?.trim()) {
            return;
        }

        try {
            removeErrorMessage(id);
            const request = isE2EBypassEnabled()
                ? Promise.resolve({ data: getMockPostalLookupResponse(normalizedPostalCode) })
                : axios.post(getEnvVar('POSTAL_CODE_LOOKUP_URL'), {postalCode: normalizedPostalCode}, {headers: getHeader()});

            request.then(res=>{
                const data = res.data; 
                if (data.status === 'OK') {
                    const address:Address = data.mailAddressChoice[0];
                    const addressVal = getAddress({...address, isDetailAddress: true, isNoStreetNoAddress: true}, false, lang);
                    setupAddressDetails(address, true);
                    setValue(addressVal);
                    setFieldValue(addressVal);
                } else {
                    addErrorMessage(id, lang === 'en' ? 'Postal code not found' : 'Code postal introuvable');
                }
            });
        } catch (error) {
            console.error("API call failed:", error);
        }
    };

    const callbackParent = () => {
        const root = getRootComponent(field);
        if (root) {
            callback(root);
        }
    }

    const selectAddress = (event: AutoCompleteSelectEvent) => {
        //setValue(event.value);
        const eventValue = event.value;

        if (!noStreetNo) {
            const addressArrayList = eventValue.split(',').map(s => s.trim());

            // Capture the street number the user typed (e.g. "4456" from "4456 romfield")
            // so it can be injected into the API response if the API omits it.
            const streetNumMatch = value?.match(/^(\d+)\s/);
            typedStreetNumberRef.current = streetNumMatch ? streetNumMatch[1] : null;

            if (streetNumMatch && addressArrayList[0] && !/^\d/.test(addressArrayList[0])) {
                addressArrayList[0] = `${streetNumMatch[1]} ${addressArrayList[0]}`;
            }

            const hasUnitOrSuite = addressArrayList[1]?.includes('Unit') || addressArrayList[1]?.includes('Suite');
            const formattedAddress = hasUnitOrSuite
                ? `${addressArrayList[1]}, ${addressArrayList[0]}, ${addressArrayList.slice(2).join(', ')}`
                : addressArrayList.join(', ');
            setSearchingAddress(formattedAddress);
        }
    }

    const AddressSearchSuggestionTemplate = (suggestion: string) => {
        return (
            <div key={suggestion} style={{width: '100%'}}>
                {suggestion}
            </div>
        )
    }

    const setupAddressDetails = (address: Address, isNoStreetNoAddress=false) => {
        let add = address;
        if (!field.suppressUnitOnAddressSearch) {
            if (address.suiteNumber && !address.unitType) {
                add = {...address, unitType: 'UNIT', suiteNumber: address.suiteNumber}
            }
        } else {
            add = {...add, suiteNumber: undefined, unitType: undefined, suiteFromNumber: undefined, suiteToNumber: undefined, streetFromNumber: undefined, streetToNumber: undefined, streetNumber: isPostalCode?undefined: address.streetNumber};
        }

        if (field.combineStreetFields && add.streetName) {
            const combinedStreetName = [add.streetName, add.streetType].filter(part => part?.trim()).join(' ').trim();
            add = {...add, streetNumber: `${field.populateUnitInStreetNumber && address.suiteNumber?`UNIT ${address.suiteNumber}, `:''}${add.streetNumber || ''} ${combinedStreetName}`.trim(), streetName: undefined, streetType: undefined};
        }
        add = {...add, isDetailAddress: true, isNoStreetNoAddress, commaAfterStreetNumber: field.commaAfterStreetNumber};
        if(add.manualAddressChecked== null || add.manualAddressChecked== undefined){
            add.manualAddressChecked=field.manualAddressChecked
        }
        setAddressDetails(add);
    }

    const addErrorMessage = (id: string, message: string) => {
        const newErrors = errors.filter(err => err.id !==id);
        newErrors.push({id, message});
        setErrors(applyConfiguredAddressErrorMessages(newErrors));
    }

    const removeErrorMessage = (id: string) => {
        setErrors(errors.filter(err => err.id !== id));
    };

    const getConfiguredAddressErrorMessage = (errorId: string, fallback: string) => {
        const configuredMap = lang === 'en' ? field.addressErrorMessages : field.addressErrorMessagesFRA;
        const configured = configuredMap?.[errorId];
        if (configured == null) {
            return fallback;
        }
        const trimmed = configured.trim();
        return trimmed.length ? trimmed : fallback;
    }

    const applyConfiguredAddressErrorMessages = (errorList: ErrorMessage[]) => {
        if (!Array.isArray(errorList) || !errorList.length) {
            return errorList;
        }
        return errorList.map((item) => ({
            ...item,
            message: getConfiguredAddressErrorMessage(item.id, item.message),
        }));
    }

    const validatNoStreetNoAddress = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = validateLotType(addressDetails, newErrors);
        newErrors = validatePropertyIdentifier(addressDetails, newErrors);
        newErrors = validateTownship(addressDetails, newErrors);
        return newErrors;
    };
    
    const validatePropertyIdentifier = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'identifier' && err.id !== 'identifierNumber')
        
        if (addressDetails.identifier && !addressDetails.identifierNumber) {
            newErrors.push({id: 'identifierNumber', message:  
                lang==='en' ? 
                `You must enter a ${ addressDetails.identifier === 'CN' 
                    ? 'concession'
                    : addressDetails.identifier === 'PN' 
                    ? 'plan'
                    : ''} number` 
                : `Vous devez entrer le numéro ${ addressDetails.identifier === 'CN' 
                    ? 'de concession'
                    : addressDetails.identifier === 'PN' 
                    ? 'de plan'
                    : ''}`
            });
        }
        if (!addressDetails.identifier) {
            newErrors.push({id: 'identifier', message: `${lang==='en' ? 'You must select either concession number or plan number' : 'Vous devez sélectionner soit le numéro de concession, soit le numéro de plan'}`});
        }
        return newErrors;
      }
    
    const validateLotType = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'lotType' && err.id !== 'lotNumber');
        if (!addressDetails.lotNumber) {
          newErrors.push({id: 'lotNumber', message: `${lang==='en' ? 'Enter your Lot number' : 'Entrez votre numéro de lot'}`});
        }
        if (!addressDetails.lotType) {
          newErrors.push({id: 'lotType', message: `${lang==='en' ? 'Select your Lot Type' : 'Entrez votre numéro de lot'}`});
        }
        return newErrors;
      }

    const validateUnitType = (addressDetails: Address, newErrors: {id: string, message: string}[] = []) => {
        newErrors = newErrors.filter(err => err.id !== 'unitType' && err.id !== 'suiteNumber');
        if (addressDetails.unitType && !addressDetails.suiteNumber) {
            newErrors.push({id: 'suiteNumber', message: `${lang==='en' ? 'You must enter a unit number when selecting a unit type': 'Vous devez entrer un numéro d’unité lorsque vous sélectionnez un type d’unité.'}`});
        }
        if (!addressDetails.unitType && addressDetails.suiteNumber) {
          newErrors.push({id: `unitType`, message: `${lang==='en' ? 'You must select a unit type when unit number is not blank': 'Vous devez sélectionner le type d’unité lorsque le champ du numéro d’unité n’est pas vide.'}`})
        }

        if (addressDetails.unitType && addressDetails.suiteNumber) {
            const suiteNum = parseInt(addressDetails.suiteNumber);
            const suiteFrom = parseInt(addressDetails.suiteFromNumber);
            const suiteTo = parseInt(addressDetails.suiteToNumber);
        
            if (!isNaN(suiteNum) && ((!isNaN(suiteFrom) && suiteNum < suiteFrom) || (!isNaN(suiteTo) && suiteNum > suiteTo))) {
                newErrors.push({id: 'suiteNumber', message: `${lang==='en' ? (translations['EN']?.find(item => item.value === addressDetails.unitType)?.text || 'Unit') + ' number is out of range' : 'Le numéro de '+ (translations['FR']?.find(item => item.value === addressDetails.unitType)?.text.toLowerCase() || 'unité') +' est hors de portée'}`});
            }
        }
        return newErrors;
    }

    const validateRoute = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'routeType' && err.id !== 'routeNumber');
        if (addressDetails.routeType && !addressDetails.routeNumber) {
            newErrors.push({id: 'routeNumber', message: `${lang === 'en' ? 'You must enter route number when route type is not blank' : "Vous devez sélectionner le type de route lorsque le numéro de route n’est pas laissé vide."}`});
        }
        if (!addressDetails.routeType && addressDetails.routeNumber) {
            newErrors.push({id: 'routeType', message: `${lang === 'en' ? 'You must select route type when route number is not blank' : "Vous devez sélectionner le type d'itinéraire lorsque le numéro d'itinéraire n'est pas vide"}`});
        }
        return newErrors;
    }
    const validateTownship = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'township');
        if (field.hideTownship) return newErrors;
        // if(!field.touchedAddressFields.includes('township')) return newErrors;
        if (!addressDetails.township) {
            newErrors.push({id: 'township', message: `${lang === 'en' ? 'Enter your Township' : 'Entrez votre canton'}`})
        }
        return newErrors;
    }
    
    const validatAddress = (addressDetails: Address, newErrors: ErrorMessage[]): ErrorMessage[] => {
        newErrors = newErrors.filter(err => err.id !== 'streetNumber' && err.id !== 'streetName')
        if (!addressDetails.streetNumber) {
            newErrors.push({id: 'streetNumber', message: `${lang==='en' ? 'You must enter street number' : 'Vous devez entrer le numéro de rue'}`});
        } else if (!field.freeTextStreetNumber && !field.combineStreetFields) {
            const streetNum = parseInt(addressDetails.streetNumber);
            const streetFrom = parseInt(addressDetails.streetFromNumber);
            const streetTo = parseInt(addressDetails.streetToNumber);
        
            if (!isNaN(streetNum) && ((!isNaN(streetFrom) && streetNum < streetFrom) || (!isNaN(streetTo) && streetNum > streetTo))) {
                newErrors.push({id: 'streetNumber', message: `${lang==='en' ? 'Your entered street number is out of range' : 'Votre numéro de rue saisi est hors de portée'}`});
            }
        }
        if (!field.combineStreetFields && !addressDetails.streetName) {
          newErrors.push({id: 'streetName', message: `${lang==='en' ? 'You must enter street name' : 'Vous devez entrer un nom de rue.'}`});
        }
        return newErrors;
    }

    const validateCityProvince = (addressDetails, newErrors) => {
        if (!addressDetails.municipality) {
            newErrors = newErrors.filter(error => error.id !== 'municipality');
            newErrors.push({id: 'municipality', message: `${lang==='en' ? 'Municipality is missing': 'La municipalité manque'}`});
        }
        if (!addressDetails.province) {
            newErrors = newErrors.filter(error => error.id !== 'province');
            newErrors.push({id: 'province', message: `${lang==='en' ? 'Province is missing': 'La province manque'}`});
        }
        return newErrors;
    }
    
    const validatAddressDetails = (): ErrorMessage[] => {
        const poBoxRegex = /^P\.?\s*O\.?\s*BOX$/i;
        let newErrors: ErrorMessage[] = [];
        if (!addressDetails) {
          return [];
        }
        if (!addressDetails.isDetailAddress && !field.allowPartialAddress) {
            return [];
        }
        if (field.addressType === 'Mailing' && field.mailingSameAsResidential) {
            return [];
        }

        if (allowManualEntry) {
            if (!field.isMunicipalityOptional && !addressDetails.municipality) {
            newErrors = newErrors.filter(error => error.id !== 'municipality');
            newErrors.push({id: 'municipality', message: `${lang==='en' ? 'City/town is a mandatory field.': 'Ville est un champ obligatoire.'}`});
            }
            if (!field.isProvinceOptional && !addressDetails.province) {
                newErrors = newErrors.filter(error => error.id !== 'province');
                newErrors.push({id: 'province', message: `${lang==='en' ? 'Province is a mandatory field': 'Province est un champ obligatoire.'}`});
            }
            if (addressDetails.province !== 'OTHER'&& !field.isPostCodeOptional && !addressDetails.postalCode) {
                newErrors = newErrors.filter(error => error.id !== 'postalCode');
                newErrors.push({id: 'postalCode', message: `${lang==='en' ? 'Postal code is a mandatory field and is required in the format A#A #A#.' : 'Code postal est un champ obligatoire; veuillez suivre le format A#A #A#.'}`});
            }
            if (addressDetails.province !== 'OTHER' &&addressDetails.postalCode && addressDetails.postalCode.trim()) {
                const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
                if (!postalCodeRegex.test(addressDetails.postalCode.trim())) {
                newErrors = newErrors.filter(error => error.id !== 'postalCode');
                newErrors.push({id: 'postalCode', message: `${lang==='en' ? 'Postal code is required in the format A#A #A#.' : 'Code postal doit être exprimé dans le format A#A #A#.'}`});
                }
            }
            if(field.isOtherOptional && addressDetails.province ==='OTHER' && !addressDetails.provinceOther){
                newErrors = newErrors.filter(error => error.id !== 'provinceOther');
                newErrors.push({ id: 'provinceOther', message: lang === 'en' ? 'Other (please specify) is a mandatory field and is required' : 'Autre (veuillez préciser) est un champ obligatoire' });
            } else if(field.isOtherOptional && addressDetails.province ==='OTHER' && addressDetails.postalCode){
                if (!validator.isLength(addressDetails.postalCode.trim(), { min: 1, max: 15 })) {
                    newErrors = newErrors.filter(error => error.id !== 'postalCode');
                    newErrors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code must be between 1 and 15 characters.' : 'Le code postal doit comporter entre 1 et 15 caractères.' });
                }
            }
          
            if (!field.isPostCodeOptional && addressDetails.province === 'OTHER' &&(!addressDetails.postalCode || !addressDetails.postalCode.trim())) {
                    newErrors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code is a mandatory field and is required ' : 'Code postal est un champ obligatoire; veuillez suivre le format A#A #A#.' });
                } else if (addressDetails.province !== 'OTHER' && addressDetails.postalCode && addressDetails.postalCode.trim()) {
                const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
                    if (!postalCodeRegex.test(addressDetails.postalCode.trim())) {
                        newErrors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code is required in the format A#A #A#.' : 'Code postal doit être exprimé dans le format A#A #A#.' });
                    }
                }
                if (!noStreetNo && !field.isStreetNumberOptional && !addressDetails.streetNumber) {
                newErrors = newErrors.filter(error => error.id !== 'streetNumber');
                newErrors.push({id: 'streetNumber', message: `${lang==='en' ? 'Street number is a mandatory field': 'Numéro de la rue est un champ obligatoire'}`});
            }
                if (!noStreetNo && !field.combineStreetFields && !field.isStreetNameOptional && !addressDetails.streetName) {
                newErrors = newErrors.filter(error => error.id !== 'streetName');
                newErrors.push({id: 'streetName', message: `${lang==='en' ? 'Street name is a mandatory field' : 'Nom de la rue est un champ obligatoire'}`});
            }
            if (noStreetNo) {
                newErrors = validatNoStreetNoAddress(addressDetails, newErrors);
            }
            const mappedErrors = applyConfiguredAddressErrorMessages(newErrors);
            setErrors(mappedErrors);
            return mappedErrors;
        }

        if (field.allowPartialAddress) {
            newErrors = validateCityProvince(addressDetails, newErrors);
            const mappedErrors = applyConfiguredAddressErrorMessages(newErrors);
            setErrors(mappedErrors);
            return mappedErrors;
        }
    
        if (!noStreetNo) {
            newErrors = validatAddress(addressDetails, newErrors);
            newErrors = validateRoute(addressDetails, newErrors);
        } else {
            newErrors = validatNoStreetNoAddress(addressDetails, newErrors);
        }
    
        newErrors = validateUnitType(addressDetails, newErrors);
        newErrors = validateCityProvince(addressDetails, newErrors);
        if (!addressDetails.postalCode) {
            newErrors = newErrors.filter(error => error.id !== 'postalCode');
            newErrors.push({id: 'postalCode', message: `${lang==='en' ? 'Postal code is missing': 'Le code postal est manquant'}`});
        }
        //setFiltefield.Valid = newErrors.length === 0;
        const mappedErrors = applyConfiguredAddressErrorMessages(newErrors);
        setErrors(mappedErrors);
        return mappedErrors;
    }

    useEffect(() => {
        if (addressDetails?.postalCode && noStreetNo) {
            setValue(addressDetails.postalCode);
        }
        return () => {
            console.log(`clear timeouts: ${timeoutIds}`);
            if (timeoutIds.length) {
                timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
            }
        }
    }, []);
    
    useEffect(() => {
        if (!value && addressString) {
            setValue(addressString);
        }
    }, [addressString, value]);

    
useEffect(() => {
     
    if( field.Address && !manualEntry /*&& field.manualAddressChecked */){
        const displayAddress = getAddress(field.Address);
        setFieldValue(displayAddress);
    }
    }, [profile, field.Address, addressDetails, noStreetNo, field.Value]);

    // useEffect(() => {
    //     if(!field.Address && !field.Value){
    //         console.log('address values emptied')
    //         addressString="";
    //         setValue("");
    //         setAddressDetails(null);
    //     }
    // }, [field.Address, field.Value]);
    
    useEffect(()=>{
        if(!profile){
            //setFieldValue('');
            setSearchingAddress('');
            setValue('');
            setAddressDetails(null);
        } else if (field.addressType === 'Mailing' && field.mailingSameAsResidential) {
            const residential = findMatchedAddressElement(field);
            const add = structuredClone(JSON.stringify(profile?.address) === '{}' ? residential.Address : profile.address) as Address;
            setAddressDetails(add);
            setFieldValue(getAddress(add));
            setNoStreeNo(add?.isNoStreetNoAddress);
        }
    },[profile]);

    useEffect(() => {
        if (!loading && !error && data) {
            if (data.mailAddressChoice?.addressDetails || data.mailAddressChoice?.length === 1) {
                const searchedAddress = data.mailAddressChoice?.addressDetails || data.mailAddressChoice?.[0];

                // If the API didn't return a street number but the user typed one,
                // inject it so the detail fields are pre-populated correctly.
                if (!searchedAddress.streetNumber && typedStreetNumberRef.current) {
                    searchedAddress.streetNumber = typedStreetNumberRef.current;
                    typedStreetNumberRef.current = null;
                }

                const displayAddress = getAddress({...searchedAddress, isDetailAddress: true, isNoStreetNoAddress: noStreetNo}, false, lang);
                setupAddressDetails(searchedAddress, noStreetNo);
                setValue(displayAddress);
                setFieldValue(displayAddress);
                //callbackParent();
            // } else if (data.mailAddressChoice.constructor === Array && data.mailAddressChoice.length) {
            //     const address:Address = data.mailAddressChoice[0];
            //     const addressVal = getAddress({...addressDetails, isNoStreetNoAddress: true}, false, lang); // Utils.getAddressStringFromDetails(address, true, lang);
            //     setupAddressDetails(address, true);
            //     setValue(addressVal);
            //     setFieldValue(addressVal);
            } else {
                setValue(addressString);
                setSuggestions(data.mailAddressChoice?.addressAlternatives ?? []);
            }
        }
    }, [data, error]);

      useEffect(() => {
            if (!field.Address?.manualAddressChecked) return;
            if (!addressDetails) return;
            if (allowManualEntry) {
                if (!noStreetNo && !field.combineStreetFields && !field.isStreetNameOptional && (!addressDetails.streetName || !addressDetails.streetName.trim())) {
                    errors.push({ id: 'streetName', message: lang === 'en' ? 'Street name is a mandatory field' : 'Le nom de rue est un champ obligatoire' });
                }
                if (!field.isPostCodeOptional && addressDetails.province !== 'OTHER' &&(!addressDetails.postalCode || !addressDetails.postalCode.trim())) {
                    errors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code is a mandatory field and is required in the format A#A #A#.' : 'Code postal est un champ obligatoire; veuillez suivre le format A#A #A#.' });
                } else if (addressDetails.province !== 'OTHER' && addressDetails.postalCode && addressDetails.postalCode.trim()) {
                    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
                    if (!postalCodeRegex.test(addressDetails.postalCode.trim())) {
                        errors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code is required in the format A#A #A#.' : 'Code postal doit être exprimé dans le format A#A #A#.' });
                    }
                }

                if (!field.isPostCodeOptional && addressDetails.province === 'OTHER' &&(!addressDetails.postalCode || !addressDetails.postalCode.trim())) {
                    errors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code is a mandatory field and is required ' : 'Code postal est un champ obligatoire; veuillez suivre le format A#A #A#.' });
                } else if (addressDetails.province !== 'OTHER' && addressDetails.postalCode && addressDetails.postalCode.trim()) {
                const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
                    if (!postalCodeRegex.test(addressDetails.postalCode.trim())) {
                        errors.push({ id: 'postalCode', message: lang === 'en' ? 'Postal code is required in the format A#A #A#.' : 'Code postal doit être exprimé dans le format A#A #A#.' });
                    }
                }
            
                if (!field.isMunicipalityOptional && (!addressDetails.municipality || !addressDetails.municipality.trim())) {
                    errors.push({ id: 'municipality', message: lang === 'en' ? 'City/town is a mandatory field.' : 'Ville est un champ obligatoire.' });
                }
                if (!field.isProvinceOptional && (!addressDetails.province || !addressDetails.province.trim())) {
                    errors.push({ id: 'province', message: lang === 'en' ? 'Province is a mandatory field' : 'La province est un champ obligatoire' });
                }

                if (!noStreetNo && !field.isStreetNumberOptional && (!addressDetails.streetNumber || !addressDetails.streetNumber.trim())) {
                    errors.push({ id: 'streetNumber', message: lang === 'en' ? 'Street number is a mandatory field' : 'Numéro de la rue est un champ obligatoire' });
                }

         }
        }, [field.Address?.manualAddressChecked, addressDetails ]);

    useEffect(() => {
        if (!e2eBypass) return;

        const mocked = {
            ...getMockAddressDetails(),
            isNoStreetNoAddress: noStreetNo,
            manualAddressChecked: field?.Address?.manualAddressChecked ?? field?.manualAddressChecked ?? false,
            isDetailAddress: true,
        } as Address;

        field.Valid = true;
        setFieldValid(true);
        setValidationErrorMessage([]);
        setErrors([]);

        if (!field.Address || !field.Address.isDetailAddress) {
            setFieldAddress(mocked);
            field.Address = mocked;
            const displayAddress = getAddress(mocked, false, lang);
            setFieldValue(displayAddress);
            setValue(displayAddress);
        }
    }, [e2eBypass, noStreetNo, lang]);

    useEffect(() => {
        if (errors.length > 0) {
            setErrors([]);
        }
        if (e2eBypass) {
            field.Valid = true;
            setFieldValid(true);
            setValidationErrorMessage([]);
            return;
        }
        if (addressDetails?.isDetailAddress && (addressDetails.postalCode || addressDetails.province)) {//&& !noStreetNo
            const add = {...addressDetails, isNoStreetNoAddress: noStreetNo, isDetailAddress: true}
            const errs = validatAddressDetails();
            const addressVal = addressDetails? 
                getAddress({...addressDetails, isNoStreetNoAddress: noStreetNo, commaAfterStreetNumber: field.commaAfterStreetNumber}, false, lang) : 
                getAddress(profile?.address, false, lang); 
            setFieldValue(addressVal);
            setValue(addressVal);
            setFieldValid(errs.length === 0);
            if (errs.length > 0) {
                console.log("validation failed");
                setValidationErrorMessage(errs)
            } else {
                setValidationErrorMessage([]);
            }
            if (addressDetails) setFieldAddress(add);
            if (field.addressType === 'Residential') {
                const element = findMatchedAddressElement(field);
                if (element && element.mailingSameAsResidential) {
                    const clone = structuredClone(add);
                    element.Address = add;
                    element.Value = getAddress(add, add.isNoStreetNoAddress, lang);
                }
            }else if(field.addressType === "Mailing"){
                const element = findMatchedAddressElement(field); //residential
                if (field.mailingSameAsResidential) {
                    field.Address = element.Address;
                    field.Value = getAddress(field.Address, sameMailing, lang);
                }
            }
            callback(getRootComponent(field));
            //console.log(Utils.getAddress(addressDetails, true));
        } else if (addressDetails && !addressDetails.isDetailAddress) {
            const province =
                addressDetails.province === 'OTHER'
                    ? addressDetails.provinceOther ?? ''
                    : addressDetails.province ?? '';
            const city = addressDetails.municipality ?? '';
            const addressVal = [city, province].filter(part => part && part.trim().length > 0).join(', ');
            const newErrors: ErrorMessage[] = [];
            if (!city.trim()) {
                newErrors.push({
                    id: 'municipality',
                    message: lang === 'en' ? 'Municipality is required' : 'La municipalité est obligatoire',
                });
            }
            if (!province.trim()) {
                newErrors.push({
                    id: 'province',
                    message: lang === 'en' ? 'Province is required' : 'La province est obligatoire',
                });
            }
            const mappedErrors = applyConfiguredAddressErrorMessages(newErrors);
            setErrors(mappedErrors);
            const isValid = mappedErrors.length === 0;
            setFieldValid(isValid);
            field.Valid = isValid;
            setValidationErrorMessage(mappedErrors);
            if (isValid) {
                removeErrorMessage('searchInput');
            }
            setFieldValue(addressVal);
            setValue(addressVal);
            setFieldAddress(addressDetails);
            if (isValid) {
                setValidationErrorMessage([]);
            }
        }
    }, [addressDetails, lang, noStreetNo, e2eBypass]);

    useEffect(() => {
        if (manualEntry) {
            const addressVal = addressDetails
                ? getAddress({ ...addressDetails, isNoStreetNoAddress: noStreetNo, commaAfterStreetNumber: field.commaAfterStreetNumber }, false, lang)
                : '';
            setValue(addressVal);
            setFieldValue(addressVal);
        }
    }, [manualEntry, addressDetails, noStreetNo, lang]);

    useEffect(() => {
        if (!addressDetails) return;
        setErrors(prev => prev.filter(err => {
            if (err.id === 'municipality' && addressDetails.municipality?.trim()) return false;
            if (err.id === 'province' && (addressDetails.province?.trim() || addressDetails.provinceOther?.trim())) return false;
            return true;
        }));
    }, [addressDetails?.municipality, addressDetails?.province, addressDetails?.provinceOther]);

    // Filter errors based on optional field flags
    useEffect(() => {
        if (allowManualEntry) {
            setErrors(prevErrors => {
                if (!prevErrors || prevErrors.length === 0) return [];
                
                let filteredErrors = [...prevErrors];
                
                if (field.isStreetNameOptional) {
                    filteredErrors = filteredErrors.filter(err => err.id !== 'streetName');
                }
                if (field.isStreetNumberOptional) {
                    filteredErrors = filteredErrors.filter(err => err.id !== 'streetNumber');
                }
                if (field.isPostCodeOptional && field.Address &&  !field.Address?.postalCode) {
                    filteredErrors = filteredErrors.filter(err => err.id !== 'postalCode');
                }
                if (field.isMunicipalityOptional) {
                    filteredErrors = filteredErrors.filter(err => err.id !== 'municipality');
                }
                if (field.isProvinceOptional) {
                    filteredErrors = filteredErrors.filter(err => err.id !== 'province');
                }
                
                return filteredErrors;
            });}
    }, [errors.length, field.isStreetNameOptional, field.isStreetNumberOptional, field.isPostCodeOptional, field.isMunicipalityOptional, field.isProvinceOptional]);

    const handleNostreetNoChange = (e: ChangeEvent<HTMLInputElement>) =>{
        const checked = e.target.checked;
        field.touchedAddressFields = field.touchedAddressFields ?? [];
        removeErrorMessage(`searchInput`);
        setNoStreeNo(checked);
        if (checked) {
            setValue(addressDetails?.postalCode ?? profile?.address?.postalCode ?? '');
            setFieldValue(getAddress(addressDetails, false, lang));
        } else {
            const addressVal = addressDetails? getAddress({...addressDetails, isNoStreetNoAddress: false}, false, lang)
            : getAddress(profile?.address, false, lang);
            setFieldValue(addressVal);
            setValue(addressVal);
        }
        field.Touched=false;
        //callback(getRootComponent(field));
    };

    const handleUnknownAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setUnknownAddressChecked(checked);
        field.unknownAddressChecked = checked;
        setValue('');
        setFieldValue('');
        setSuggestions([]);
        setSearchingAddress('');
        toggleManualEntry(checked, field);
    };

    const handleSameMailingChange = (e: ChangeEvent<HTMLInputElement>) =>{
        const checked = e.target.checked;
        removeErrorMessage(`searchInput`);
        setSameMailing(checked);
        let add = null;
        if (checked) {
            const residential = findMatchedAddressElement(field);
            if (residential?.Address && residential.Valid) {
                add = structuredClone(residential.Address) as Address;
                const details = getAddress(add, add?.isNoStreetNoAddress, lang);
                setAddressDetails(add);
                setFieldValue(details);
            } else {
                setAddressDetails(null);
                setFieldValue('');
                setFieldValid(true);
            }
        } else {
            setAddressDetails(null);
            setValue('');
            setFieldValue('');
            setFieldValid(true);
            setErrors([]);
            field.Valid = true;
        }
        setFieldAddress(add);
        setSameAsResidential(checked);
        callback(getRootComponent(field));
    }

    const toggleManualEntry = (checked: boolean, field: FormSectionElements) => {
        setManualEntry(checked);
        removeErrorMessage('searchInput');
        setSuggestions([]);
        setFieldValid(true);
        setErrors([]);
        setSearchingAddress('');

        if (checked) {
            if (field.clearAddressOnManualCheck) {
                // Clear the address lookup field and all manual entry fields
                setValue('');
                setFieldValue('');
                setManualAddressInitialized(true);
                const newAddress: Address = {
                    isDetailAddress: true,
                    isNoStreetNoAddress: noStreetNo,
                    manualAddressChecked: true,
                    commaAfterStreetNumber: field.commaAfterStreetNumber,
                } as Address;
                setAddressDetails(newAddress);
                setFieldAddress(newAddress);
                field.manualAddressChecked = true;
                field.Touched = true;
                callbackParent();
            } else if (!addressDetails && !field.Address) {
              setManualAddressInitialized(true);
               const newAddress: Address = {
                ...addressDetails,
                isDetailAddress: true,
                isNoStreetNoAddress: noStreetNo,
                manualAddressChecked: true,
                commaAfterStreetNumber: field.commaAfterStreetNumber,
            };
                setAddressDetails(newAddress);
                field.manualAddressChecked = true;
                field.Touched = true;
                setFieldAddress(newAddress);
                callbackParent();
             
            } else {
                setManualAddressInitialized(false);
                  const newAddress: Address = {
                    ...addressDetails,
                     isDetailAddress: true,
                    isNoStreetNoAddress: noStreetNo,
                    manualAddressChecked: true,
                    commaAfterStreetNumber: field.commaAfterStreetNumber,
             };
                setAddressDetails(newAddress);
                setFieldAddress(newAddress);
                field.manualAddressChecked= true;
                field.Touched = true;
                callbackParent();
            }
        } else  {
            field.manualAddressChecked= false;
            field.Touched = false
            clearAddressDetails();
            setManualAddressInitialized(false);
            setValue('');
            setFieldValue('');
            setManualEntry(false);
          
        }
    }

    const clearAddressDetails = () => {
        setAddressDetails(null);
        // setNoStreeNo(null);
        setSuggestions([]);
        setFieldValid(true);
        setErrors([]);
        setSearchingAddress('');
        setFieldAddress(null);
        field.Touched = true;
        callbackParent();
    }   

    const handleSearchWords = (e: AutoCompleteChangeEvent) => {
        // if(e.value==""){
        //     return;
        // }
        const value = e.target.value;
        setValue(value);
        setFieldValue(value);
        if (!value || value.length < 3) {
            removeErrorMessage('searchInput');
            clearAddressDetails();
        }
        //callbackParent();
    }

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // field.Touched = true;
        if (field.Value && field.Touched) {
            const blurId = setTimeout(() => {
                FormValidationUtils.validateAddress(field);
                callback(getRootComponent(field));
            }, searchDelay + 10);

            setTimeoutIds(prev => [...prev, blurId]);
        } else {
            FormValidationUtils.validateAddress(field);
            callback(getRootComponent(field));
        }
    };

    const onKeyDownScrollHighlight = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            setTimeout(() => {
                const input = document.getElementById(field.LoadBindName) as HTMLInputElement | null;
                const panelId = input?.getAttribute('aria-controls');
                const panel = panelId ? document.getElementById(panelId) : null;
                const highlighted = panel?.querySelector('li.p-highlight') as HTMLElement | null;
                if (highlighted) {
                    highlighted.scrollIntoView({ block: 'nearest' });
                }
            }, 0);
        }
    };

    const setTouchedAddressFields= (fieldName)=> {
        if(!field.touchedAddressFields.includes(fieldName))
            field.touchedAddressFields.push(fieldName);
    }

    return (
        <div style={{marginBottom: '2.75rem'}}>

            {((field.showMailingDetails || !sameMailing) || field.addressType !== "Mailing") && (
                <div className={`auto-suggestions-search ${!field.Valid? 'auto-suggestions-search-error' : ''}`}>
                 <p><span style={{color: colors.hintTextGrey}}>{noStreetNo? propts.noStreetNo[lang]: (isBxpForm(field) ? propts.BXPstreetNo[lang] : propts.streetNo[lang])}</span></p>
                    {allowManualEntry && (
                        <ErrorElement
                            display={!!errors.filter(err => err.id === 'searchInput').length} 
                            message={errors.filter(err => err.id === 'searchInput')?.[0]?.message}
                            id={field.LoadBindName + '_error'}
                        />
                    )}
                    {/* @ts-ignore */}
                    <AutoComplete
                        className={`auto-complete ${!field.Valid && !field.Value ? 'auto-complete-error' : ''}`}
                        field={fieldName} 
                        inputId={field.LoadBindName}
                        value={value}
                        suggestions={suggestions}
                        completeMethod = {search}
                        onChange={handleSearchWords}
                        onBlur={onBlur}
                        itemTemplate ={AddressSearchSuggestionTemplate}
                        onSelect={selectAddress}
                        disabled={field.ReadOnly || field.mailingSameAsResidential || manualEntry}
                        appendTo={document.body}
                        panelStyle={{ maxHeight: '260px', overflowY: 'auto' }}
                        onKeyDown={onKeyDownScrollHighlight}
                    />
                    {field.allowManualAddress && (
                        <div className='ontario-checkboxes__item ontario-checkboxes__single-option' style={{ marginTop: '0.5rem' }}>
                            <input
                                className='ontario-checkboxes__input'
                                type='checkbox'
                                id={`fdIpt_manual_entry_${field.Id}`}
                                checked={manualEntry}
                                onChange={e => toggleManualEntry(e.target.checked, field)}
                                disabled={field.ReadOnly || (field.restrictManualAndNoStreet && noStreetNo)}
                                style={{ width: '28px', height: '28px' }}
                            />  
                            <span
                                className='ontario-checkboxes__label'
                                style={{ display: 'inline-flex', flex: '0 0 auto', cursor: 'default', pointerEvents: 'none' }}
                            >
                                {manualAddressEntryLabel}
                            </span>
                        </div>
                    )}
                </div>
            )}
            {field.addressType!== 'Mailing' && !field.hideStreetNumberNameCheckbox && (
                <div className='ontario-checkboxes__item ontario-checkboxes__single-option'>
                    <input
                        className='ontario-checkboxes__input'
                        type='checkbox'
                        name='noStreetNoOption'
                        id={`fdIpt_no_streeNo_${field.Id}`}
                        title={field.addressType=== 'Business' ? propts.isBusiness[lang]: propts.title[lang]}
                        checked={noStreetNo}
                        onChange={handleNostreetNoChange}
                        disabled={field.ReadOnly || (field.restrictManualAndNoStreet && manualEntry)}
                    />
                    <label
                        className='ontario-checkboxes__label'
                        htmlFor={`fdIpt_no_streeNo_${field.Id}`}
                        style={{ display: 'inline-flex', flex: '0 0 auto' }}
                    >
                        {field.addressType=== 'Business' ? propts.isBusiness[lang]: propts.title[lang]}
                    </label>
                </div>
            )}
            {field.addressType!== 'Mailing' && !field.hideStreetNumberNameCheckbox && field.allowUnknownAddress && (
                <div className='ontario-checkboxes__item ontario-checkboxes__single-option'>
                    <input
                        className='ontario-checkboxes__input'
                        type='checkbox'
                        name='unknownAddress'
                        id={`fdIpt_unknown_address_${field.Id}`}
                        title={propts.unknownAddress[lang]}
                        checked={unknownAddressChecked}
                        onChange={handleUnknownAddressChange}
                        disabled={field.ReadOnly}
                    />
                    <label
                        className='ontario-checkboxes__label'
                        htmlFor={`fdIpt_unknown_address_${field.Id}`}
                        style={{ display: 'inline-flex', flex: '0 0 auto' }}
                    >
                        {propts.unknownAddress[lang]}
                    </label>
                </div>
            )}

            {field.addressType==='Mailing' && !field.hideStreetNumberNameCheckbox && (
                <div className='ontario-checkboxes__item ontario-checkboxes__single-option'>
                    <input
                        className='ontario-checkboxes__input'
                        type='checkbox'
                        name='sameMailing'
                        id={`fdIpt_same_mailing_${field.Id}`}
                        title={propts.sameMaling[lang]}
                        checked={sameMailing}
                        onChange={handleSameMailingChange}
                        disabled={field.ReadOnly}
                    />
                    <label
                        className='ontario-checkboxes__label'
                        htmlFor={`fdIpt_same_mailing_${field.Id}`}
                        style={{ display: 'inline-flex', flex: '0 0 auto' }}
                    >
                      {propts.sameMaling[lang]}
                    </label>
                </div>
            )}
            
            {field.hintExpansionTitle && ((field.showMailingDetails || !sameMailing) || field.addressType !== "Mailing")&& (
                <div style={{maxWidth: "420px"}}>
                    <HintExpansionComponent
                        isEng={true}
                        calloutType={field.callOutBoxType}
                        Visible={field.Visible}
                        bodyMsg={lang === "en"?field.callOutBoxBody: field.callOutBoxBodyFRA}
                        title={lang === "en"? field.callOutBoxTitle:field.callOutBoxTitleFRA}
                        hintExpansionTitle={lang === "en"? field.hintExpansionTitle:field.hintExpansionTitleFRA}
                        field={field}/>
                </div>)}

            {(!field.addressType || field.addressType!=='Mailing' || (field.addressType==='Mailing' && ((field.mailingSameAsResidential && field.showMailingDetails) || !field.mailingSameAsResidential)) || allowManualEntry) && (
                <div className='address-detail-container'>
                    <AddressDetails
                        id={field.Id} 
                        lang={lang} 
                        noStreetNoAddress={noStreetNo}
                        setAddressDetails={setAddressDetails}
                        errors={errors}
                        addressDetails={addressDetails ?? field.Address}
                        readOnly={field.ReadOnly || (field.addressType==='Mailing' && ((field.mailingSameAsResidential && field.showMailingDetails)))}
                        hideAddressDetails={field.hideAddressDetails}
                        hideUnitNumber={field.hideUnitNumber}
                        hideStreetNumberSuffix={field.hideStreetNumberSuffix}
                        hideUnitType={field.hideUnitType}
                        hideTownship={field.hideTownship}
                        allowManualAddress={allowManualEntry}
                        allowPartialAddress={field.allowPartialAddress}
                        alwaysDisplayCity={field.AlwaysDisplayCity}
                        alwaysDisplayProvince={field.AlwaysDisplayProvince}
                        canadianAddress={field.CanadianAddress}
                        hideCountry={field.hideCountry}
                        showCountryField={field.showCountryField}
                        hideStreetDirection={field.hideStreetDirection}
                        hideStreetType={field.hideStreetType}
                        isBXPaddress={field.isBXPaddress}
                        hideRuralRoute={field.hideRuralRoute}
                        isOtherOptional={field.isOtherOptional}
                         hidePOBox={field.hidePOBox}
                        isPostCodeOptional={field.isPostCodeOptional}
                        isMunicipalityOptional={field.isMunicipalityOptional}
                        isProvinceOptional={field.isProvinceOptional}
                        isStreetNumberOptional={field.isStreetNumberOptional}
                        freeTextStreetNumber={field.freeTextStreetNumber}
                        isStreetNameOptional={field.isStreetNameOptional}   
                        hideStreetNumberNameCheckbox={field.hideStreetNumberNameCheckbox}
                        lockStreetFieldsAfterPopulate={field.lockStreetFieldsAfterPopulate}
                        manualAddressChecked={field.manualAddressChecked}
                        autoSetCountryFromProvince={field.autoSetCountryFromProvince}
                        useTouchedForNonBxpErrors={field.useTouchedForNonBxpErrors}
                        addressFieldLabels={field.addressFieldLabels}
                        addressFieldLabelsFRA={field.addressFieldLabelsFRA}
                        hideAddressFieldRequirementIndicators={field.hideAddressFieldRequirementIndicators}
                        setTouchedAddressFields={setTouchedAddressFields}
                        combineStreetFields={!!field.combineStreetFields}
                        populateUnitInStreetNumber={!!field.populateUnitInStreetNumber}
                    />
                </div>
            )}
        </div>
    )
}

export default AddressSearchComponent;

